import React from "react";

/**
 * Subtle module-specific background pattern. 3–5% opacity, absolutely positioned,
 * pointer-events-none so it never interferes with interaction.
 *
 * Usage:
 *   <div className="relative">
 *     <Pattern kind="finance" />
 *     ...content...
 *   </div>
 */
export function Pattern({ kind = "dashboard", className = "" }) {
  const p = PATTERNS[kind] || PATTERNS.dashboard;
  return (
    <svg
      aria-hidden="true"
      className={`absolute inset-0 w-full h-full pointer-events-none text-primary opacity-[0.05] dark:opacity-[0.08] ${className}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>{p.def}</defs>
      <rect width="100%" height="100%" fill={`url(#hm-${kind})`} />
    </svg>
  );
}

const PATTERNS = {
  dashboard: {
    def: (
      <pattern id="hm-dashboard" width="60" height="60" patternUnits="userSpaceOnUse">
        <circle cx="30" cy="30" r="1.5" fill="currentColor" />
        <line x1="30" y1="30" x2="60" y2="0" stroke="currentColor" strokeWidth="0.5" />
        <line x1="30" y1="30" x2="0" y2="0" stroke="currentColor" strokeWidth="0.5" />
        <line x1="30" y1="30" x2="60" y2="60" stroke="currentColor" strokeWidth="0.5" />
        <line x1="30" y1="30" x2="0" y2="60" stroke="currentColor" strokeWidth="0.5" />
      </pattern>
    ),
  },
  health: {
    def: (
      <pattern id="hm-health" width="120" height="40" patternUnits="userSpaceOnUse">
        <path d="M0 20 L20 20 L25 5 L35 35 L45 10 L55 30 L60 20 L120 20"
              fill="none" stroke="currentColor" strokeWidth="0.8" />
      </pattern>
    ),
  },
  poultry: {
    def: (
      <pattern id="hm-poultry" width="40" height="40" patternUnits="userSpaceOnUse">
        <circle cx="10" cy="10" r="1" fill="currentColor" />
        <circle cx="30" cy="20" r="1" fill="currentColor" />
        <circle cx="15" cy="30" r="1" fill="currentColor" />
        <path d="M0 40 Q20 30 40 40" fill="none" stroke="currentColor" strokeWidth="0.5" />
      </pattern>
    ),
  },
  projects: {
    def: (
      <pattern id="hm-projects" width="40" height="40" patternUnits="userSpaceOnUse">
        <path d="M0 0 L40 0 L40 40 L0 40 Z" fill="none" stroke="currentColor" strokeWidth="0.4" />
        <path d="M8 40 L8 20 L20 12 L32 20 L32 40" fill="none" stroke="currentColor" strokeWidth="0.6" />
        <line x1="14" y1="40" x2="14" y2="30" stroke="currentColor" strokeWidth="0.5" />
        <line x1="26" y1="40" x2="26" y2="30" stroke="currentColor" strokeWidth="0.5" />
      </pattern>
    ),
  },
  finance: {
    def: (
      <pattern id="hm-finance" width="50" height="50" patternUnits="userSpaceOnUse">
        <line x1="0" y1="45" x2="50" y2="45" stroke="currentColor" strokeWidth="0.3" />
        <path d="M0 40 L10 30 L20 35 L30 15 L40 25 L50 10"
              fill="none" stroke="currentColor" strokeWidth="0.7" />
      </pattern>
    ),
  },
  inventory: {
    def: (
      <pattern id="hm-inventory" width="30" height="30" patternUnits="userSpaceOnUse">
        <rect x="4" y="4" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="0.6" />
        <rect x="17" y="4" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="0.6" />
        <rect x="4" y="17" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="0.6" />
        <rect x="17" y="17" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="0.6" />
      </pattern>
    ),
  },
  map: {
    def: (
      <pattern id="hm-map" width="80" height="80" patternUnits="userSpaceOnUse">
        <path d="M0 20 Q20 10 40 20 T80 20" fill="none" stroke="currentColor" strokeWidth="0.5" />
        <path d="M0 40 Q20 30 40 40 T80 40" fill="none" stroke="currentColor" strokeWidth="0.5" />
        <path d="M0 60 Q20 50 40 60 T80 60" fill="none" stroke="currentColor" strokeWidth="0.5" />
      </pattern>
    ),
  },
  marketing: {
    def: (
      <pattern id="hm-marketing" width="60" height="60" patternUnits="userSpaceOnUse">
        <circle cx="10" cy="10" r="2" fill="currentColor" />
        <circle cx="50" cy="15" r="2" fill="currentColor" />
        <circle cx="30" cy="45" r="2" fill="currentColor" />
        <line x1="10" y1="10" x2="50" y2="15" stroke="currentColor" strokeWidth="0.4" />
        <line x1="10" y1="10" x2="30" y2="45" stroke="currentColor" strokeWidth="0.4" />
        <line x1="30" y1="45" x2="50" y2="15" stroke="currentColor" strokeWidth="0.4" />
      </pattern>
    ),
  },
  hr: {
    def: (
      <pattern id="hm-hr" width="60" height="60" patternUnits="userSpaceOnUse">
        <circle cx="15" cy="20" r="4" fill="none" stroke="currentColor" strokeWidth="0.6" />
        <path d="M8 34 Q15 26 22 34" fill="none" stroke="currentColor" strokeWidth="0.6" />
        <circle cx="45" cy="40" r="4" fill="none" stroke="currentColor" strokeWidth="0.6" />
        <path d="M38 54 Q45 46 52 54" fill="none" stroke="currentColor" strokeWidth="0.6" />
        <line x1="19" y1="24" x2="41" y2="40" stroke="currentColor" strokeWidth="0.3" />
      </pattern>
    ),
  },
  situation: {
    def: (
      <pattern id="hm-situation" width="80" height="80" patternUnits="userSpaceOnUse">
        <circle cx="40" cy="40" r="4" fill="none" stroke="currentColor" strokeWidth="0.5" />
        <circle cx="40" cy="40" r="18" fill="none" stroke="currentColor" strokeWidth="0.4" />
        <circle cx="40" cy="40" r="32" fill="none" stroke="currentColor" strokeWidth="0.3" />
        <line x1="40" y1="8" x2="40" y2="72" stroke="currentColor" strokeWidth="0.2" />
        <line x1="8" y1="40" x2="72" y2="40" stroke="currentColor" strokeWidth="0.2" />
      </pattern>
    ),
  },
  // Executive documents ribbon — Reports, Documents, Notifications
  reports: {
    def: (
      <pattern id="hm-reports" width="60" height="60" patternUnits="userSpaceOnUse">
        <rect x="10" y="8" width="28" height="40" rx="2" fill="none" stroke="currentColor" strokeWidth="0.5" />
        <line x1="14" y1="16" x2="34" y2="16" stroke="currentColor" strokeWidth="0.4" />
        <line x1="14" y1="22" x2="34" y2="22" stroke="currentColor" strokeWidth="0.4" />
        <line x1="14" y1="28" x2="30" y2="28" stroke="currentColor" strokeWidth="0.4" />
        <line x1="14" y1="34" x2="34" y2="34" stroke="currentColor" strokeWidth="0.4" />
        <line x1="14" y1="40" x2="26" y2="40" stroke="currentColor" strokeWidth="0.4" />
      </pattern>
    ),
  },
  // Luxury estate silhouette — Assets
  assets: {
    def: (
      <pattern id="hm-assets" width="80" height="60" patternUnits="userSpaceOnUse">
        <path d="M10 50 L10 34 L24 22 L38 34 L38 50 Z" fill="none" stroke="currentColor" strokeWidth="0.6" />
        <rect x="18" y="38" width="6" height="12" fill="none" stroke="currentColor" strokeWidth="0.4" />
        <path d="M48 50 L48 30 L60 24 L72 30 L72 50 Z" fill="none" stroke="currentColor" strokeWidth="0.6" />
        <line x1="0" y1="50" x2="80" y2="50" stroke="currentColor" strokeWidth="0.3" strokeDasharray="2 3" />
      </pattern>
    ),
  },
  // Agricultural contour rows — Farm360
  farm: {
    def: (
      <pattern id="hm-farm" width="80" height="60" patternUnits="userSpaceOnUse">
        <path d="M0 15 Q20 8 40 15 T80 15" fill="none" stroke="currentColor" strokeWidth="0.5" />
        <path d="M0 30 Q20 23 40 30 T80 30" fill="none" stroke="currentColor" strokeWidth="0.5" />
        <path d="M0 45 Q20 38 40 45 T80 45" fill="none" stroke="currentColor" strokeWidth="0.5" />
        <circle cx="20" cy="15" r="0.9" fill="currentColor" />
        <circle cx="50" cy="30" r="0.9" fill="currentColor" />
        <circle cx="65" cy="45" r="0.9" fill="currentColor" />
      </pattern>
    ),
  },
  // Executive control gear — Settings, Users, Permissions, Audit
  settings: {
    def: (
      <pattern id="hm-settings" width="80" height="80" patternUnits="userSpaceOnUse">
        <circle cx="40" cy="40" r="14" fill="none" stroke="currentColor" strokeWidth="0.5" />
        <circle cx="40" cy="40" r="4" fill="none" stroke="currentColor" strokeWidth="0.5" />
        <line x1="40" y1="20" x2="40" y2="28" stroke="currentColor" strokeWidth="0.5" />
        <line x1="40" y1="52" x2="40" y2="60" stroke="currentColor" strokeWidth="0.5" />
        <line x1="20" y1="40" x2="28" y2="40" stroke="currentColor" strokeWidth="0.5" />
        <line x1="52" y1="40" x2="60" y2="40" stroke="currentColor" strokeWidth="0.5" />
        <line x1="26" y1="26" x2="31" y2="31" stroke="currentColor" strokeWidth="0.5" />
        <line x1="49" y1="49" x2="54" y2="54" stroke="currentColor" strokeWidth="0.5" />
        <line x1="26" y1="54" x2="31" y2="49" stroke="currentColor" strokeWidth="0.5" />
        <line x1="49" y1="31" x2="54" y2="26" stroke="currentColor" strokeWidth="0.5" />
      </pattern>
    ),
  },
  // Sales & Cash — coin + growth curve
  sales: {
    def: (
      <pattern id="hm-sales" width="60" height="60" patternUnits="userSpaceOnUse">
        <circle cx="18" cy="30" r="8" fill="none" stroke="currentColor" strokeWidth="0.6" />
        <text x="18" y="34" fontSize="9" fontFamily="monospace" textAnchor="middle" fill="currentColor">$</text>
        <path d="M32 44 L40 36 L48 40 L56 20" fill="none" stroke="currentColor" strokeWidth="0.6" />
        <circle cx="56" cy="20" r="1.4" fill="currentColor" />
      </pattern>
    ),
  },
};

export const MODULE_ACCENT = {
  dashboard:  { key: "primary",    hex: "#0B2E59" },
  health:     { key: "module-health",    hex: "#1E8E5A" },
  poultry:    { key: "module-poultry",   hex: "#F5A623" },
  sales:      { key: "module-poultry",   hex: "#F5A623" },
  projects:   { key: "module-projects",  hex: "#F28C28" },
  finance:    { key: "module-finance",   hex: "#0B2E59" },
  inventory:  { key: "module-inventory", hex: "#7C3AED" },
  assets:     { key: "module-assets",    hex: "#0D9488" },
  farm:       { key: "module-health",    hex: "#1E8E5A" },
  marketing:  { key: "module-marketing", hex: "#D946EF" },
  hr:         { key: "module-hr",        hex: "#6366F1" },
  reports:    { key: "module-reports",   hex: "#1E3A8A" },
  settings:   { key: "module-settings",  hex: "#64748B" },
  map:        { key: "module-assets",    hex: "#0D9488" },
  situation:  { key: "module-projects",  hex: "#F28C28" },
};
